import "./SimVerification.scss";

import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { SimCard } from "@/assets/icons/icons";
import IccidPukHint from "@/assets/onboarding/sim-verification/IccidAndPuk.svg?react";
import { IconTile } from "@/components/Checkout/IconTile/IconTile";
import { GoToShopSection } from "@/components/Checkout/Steps/SimSelection/GoToShopSection";
import { PhysicalSimFields } from "@/components/Checkout/Steps/SimSelection/PhysicalSimFields/PhysicalSimFields";
import { useSimVerificationFlow } from "@/components/Checkout/Steps/SimVerification/useSimVerificationFlow";
import { Step } from "@/components/Checkout/Steps/Step";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useIsMobile } from "@/hooks/useIsMobile";

// Re-export for existing test / import paths
export {
  formatIccid,
  type SimVerificationFormProps,
} from "./simVerificationForm";

/**
 * Standalone physical-SIM verification route (`/1/sim-verification`).
 * Kept for backwards-compatible URLs. Combined Step 1 UI lives in
 * SimSelection (including physical purchase intents); both share
 * useSimVerificationFlow + PhysicalSimFields.
 */
export const SimVerification: FC = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    register,
    setValue,
    errors,
    handleSubmit,
    pukMandatory,
    sim,
    isLoadingCheckSim,
    continueIsEnabled,
    isRoutingToNextStep,
    showOverlay,
    setShowOverlay,
    saveAndNavigateForward,
    saveAndNavigateBackward,
  } = useSimVerificationFlow();

  if (isRoutingToNextStep) {
    return (
      <Step
        id="onboarding-sim-verification"
        headline={t("Onboarding.sections.sim-selection.title")}
      >
        <LoadingSpinner />
      </Step>
    );
  }

  return (
    <Step
      id="onboarding-sim-verification"
      headline={t("Onboarding.sections.sim-selection.title")}
    >
      {isLoadingCheckSim && <LoadingSpinner />}
      <form onSubmit={handleSubmit(() => saveAndNavigateForward())}>
        <div className="content-wrapper">
          <div className="tile-and-inputs">
            <IconTile
              icon={<SimCard />}
              label={t("Tile.label.activateSim")}
              isMobile={isMobile}
              variant="sim-selection-variant"
              selected
            />
            <div className="h-8 smPlus:h-12" />
            <PhysicalSimFields
              register={register}
              errors={errors}
              setValue={setValue}
              pukMandatory={pukMandatory}
              simUnavailable={sim?.message === "Sim is not available"}
              showOverlay={showOverlay}
              setShowOverlay={setShowOverlay}
            />
          </div>
          <div className={clsx("hint-image", pukMandatory && "withPuk")}>
            <IccidPukHint />
          </div>
        </div>
        <ButtonGroupFlexBox
          forceButtonWidth
          primaryButton={
            <SubmitButton
              label={t("Common.label.forward")}
              disabled={!continueIsEnabled}
            />
          }
          secondaryButton={
            <Button
              variant="secondary"
              onClick={() => saveAndNavigateBackward()}
            >
              {t("Common.label.back")}
            </Button>
          }
        />

        {isMobile && (
          <div className="mt-8">
            <GoToShopSection />
          </div>
        )}
      </form>
    </Step>
  );
};
